import { forwardRef, useEffect, useRef } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { useDatepicker } from '@/hooks/datepicker/useDatepicker';
import useUser from '@/hooks/swr/useUser';
import { UPDATE_DATE, UPDATE_MIN_DATE } from '@/utils/hooks/datepicker/actions';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';

import PropTypes from 'prop-types';

import datepickerDateFormat from '@/lib/datepicker/dateFormat';
import { CUSTOM } from '@/lib/periods';

import Datepicker from '@/components/base/Datepicker';
import Icon from '@/components/base/Icon';
import Input from '@/components/base/Input';

const DatepickerInput = forwardRef(
    (
        {
            noBorderShadow = false,
            inputStyle = 'noBackground',
            toggleDatepicker,
            setReferenceElement,
            isLeftIcon,
            placeholder,
            periodSelected,
            minDate,
            removePeriod = () => {
                /** */
            }
        },
        ref
    ) => {
        const { dispatch } = useDatepicker();
        useEffect(() => {
            dispatch({
                type: UPDATE_DATE,
                payload: periodSelected
            });
        }, [JSON.stringify(periodSelected)]);

        useEffect(() => {
            dispatch({
                type: UPDATE_MIN_DATE,
                payload: minDate
            });
        }, [minDate]);

        return (
            <div className="relative">
                <button
                    data-testid="datepicker-input"
                    ref={setReferenceElement}
                    className="w-full focus-within:outline-none focus-within:ring-2 focus-within:ring-icoBlue-200 focus-within:ring-offset-0"
                    onClick={toggleDatepicker}>
                    <Input
                        ref={ref}
                        readOnly
                        id="datepicker"
                        placeholder={placeholder}
                        className="cursor-pointer"
                        inputStyle={inputStyle}
                        noBorderShadow={noBorderShadow}
                        beforeInput={
                            isLeftIcon && (
                                <div className="leading-none">
                                    <Icon
                                        icon="calendar"
                                        className={clsx('text-base', {
                                            'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                                inputStyle === 'datepicker_no_background',
                                            'text-icoGray-400 dark:text-icoDarkMode-wolf':
                                                inputStyle === 'datepicker'
                                        })}
                                    />
                                </div>
                            )
                        }
                        afterInput={
                            !Array.isArray(periodSelected) ? (
                                <div className="leading-none">
                                    <Icon
                                        icon="chevron-bottom"
                                        className={clsx('text-base', {
                                            'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                                inputStyle === 'datepicker_no_background',
                                            'text-icoGray-400 dark:text-icoDarkMode-wolf':
                                                inputStyle === 'datepicker'
                                        })}
                                    />
                                </div>
                            ) : null
                        }
                    />
                </button>
                {periodSelected ? (
                    <button
                        className="absolute right-5 top-1/2 z-2 flex -translate-y-1/2 transform"
                        onClick={removePeriod}>
                        <Icon
                            icon="close"
                            className={clsx('text-base', {
                                'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                    inputStyle === 'datepicker_no_background',
                                'text-icoGray-400 dark:text-icoDarkMode-wolf':
                                    inputStyle === 'datepicker'
                            })}
                        />
                    </button>
                ) : null}
            </div>
        );
    }
);

DatepickerInput.displayName = 'DatepickerInput';

DatepickerInput.Proptypes = {
    toggleDatepicker: PropTypes.func.isRequired,
    setReferenceElement: PropTypes.func.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

const DatepickerAlbums = ({
    isLeftIcon,
    placeholder,
    noBorderShadow = false,
    inputStyle = 'datepicker_no_background',
    periodSelected = null,
    removePeriod,
    setPeriodSelected,
    options = {}
}) => {
    const { user } = useUser();
    const inputRef = useRef(null);
    const { minDate, getDataRetentionDate } = useDateLimits();

    const newMinDate = minDate ?? getDataRetentionDate();

    return (
        <Datepicker
            date={periodSelected}
            placeholder={placeholder}
            period={CUSTOM}
            timezone={user?.settings?.timezone}
            options={{
                placement: 'bottom-end',
                dateFormat: datepickerDateFormat[user.settings.date_format] ?? 'Y/m/d',
                mode: 'range',
                parseDate: (dateStr) => dayjs(dateStr).toDate(),
                minDate: newMinDate,
                ...options
            }}
            onChange={(data) => {
                if (data?.date && data?.date?.length === 2) {
                    setPeriodSelected(data?.date?.map((date) => date?.slice(0, 19)));
                }
            }}>
            <DatepickerInput
                ref={inputRef}
                isLeftIcon={isLeftIcon}
                inputStyle={inputStyle}
                noBorderShadow={noBorderShadow}
                placeholder={placeholder}
                removePeriod={removePeriod}
                periodSelected={periodSelected ?? null}
                minDate={newMinDate}
            />
        </Datepicker>
    );
};

DatepickerAlbums.propTypes = {
    periodSelected: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    setPeriodSelected: PropTypes.func,
    placeholder: PropTypes.string,
    isLeftIcon: PropTypes.bool
};

export default DatepickerAlbums;
